// src/component/GroupActivity2026List.js
//
// 2026 グループ活動：4期支払い済みユーザーの「参加意思」集計。
// グラフのみで表示する（ユーザー一覧表示は廃止）。
//
// 読み込み戦略：
//  - 通常運用：where("groupActivity2026.response", "in", ["join", "decline"])
//    回答済みユーザーだけ取得。未回答（万単位）は触らない。
//  - 差分取得：where("groupActivity2026.respondedAt", ">", lastFetchedAt)
//    回答が動いた人だけマージ更新。
//  - 未回答取得：明示的なボタンで全 4期ユーザー取得（重い）。

import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Button,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DoDisturbAltRoundedIcon from "@mui/icons-material/DoDisturbAltRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import CloudDownloadRoundedIcon from "@mui/icons-material/CloudDownloadRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from "recharts";

import {
  collection,
  getDocs,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { db } from "../api/firebase";
import { useNavigate } from "react-router-dom";

import { AppThemeContext } from "./AppShell";
import { is4kiPaidUser } from "./groupActivity2026Audience";

const BUCKETS = {
  join: { label: "参加する", color: "#2e7d32", icon: CheckCircleRoundedIcon },
  decline: { label: "参加しない", color: "#616161", icon: DoDisturbAltRoundedIcon },
  pending: { label: "未回答", color: "#b08800", icon: HelpOutlineRoundedIcon },
};

// v4: 一覧 UI を廃止し、グラフのみに変更（キャッシュ構造は同じだが念のためバンプ）
const CACHE_KEY = "groupActivity2026List_cache_v4";

// ─────────────────────────────────────────────
// User document → 軽量化したレコード（キャッシュ用）
// ─────────────────────────────────────────────
function projectUser(id, data) {
  return {
    id,
    prefecture: data.prefecture || "",
    usState: data.usState || "",
    country: data.country || "",
    payments: Array.isArray(data.payments)
      ? data.payments.map((p) => ({
          paymentPhase: p?.paymentPhase || "",
          paymentMethod: p?.paymentMethod || "",
        }))
      : [],
    groupActivity2026: data.groupActivity2026
      ? {
          response: data.groupActivity2026.response || null,
          respondedAtMs:
            typeof data.groupActivity2026.respondedAt?.toMillis === "function"
              ? data.groupActivity2026.respondedAt.toMillis()
              : null,
        }
      : null,
  };
}

function loadCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.users)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveCache({ users, lastFetchedAt, pendingLoaded }) {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        users,
        lastFetchedAt: lastFetchedAt.toISOString(),
        pendingLoaded: !!pendingLoaded,
      })
    );
  } catch (e) {
    console.warn("GroupActivity2026List cache save failed:", e);
  }
}

function clearCache() {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {}
}

export default function GroupActivity2026List() {
  const navigate = useNavigate();
  const theme = useContext(AppThemeContext) || {};
  const accent = theme.railActive ?? "#5A35FF";
  const surface = theme.surfaceBg ?? "#FFFFFF";
  const textBase = theme.railText ?? "rgba(40,40,60,0.9)";
  const border = theme.bottomBorder ?? "rgba(0,0,0,0.08)";

  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [users, setUsers] = useState([]);
  const [pendingLoaded, setPendingLoaded] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  // ───────────────────────────────────────────
  // 回答済みのみ取得（軽い・通常運用）
  // ───────────────────────────────────────────
  const loadRespondedOnly = async () => {
    setSyncing(true);
    setErrorMsg("");
    const t = new Date();
    try {
      const q = query(
        collection(db, "users"),
        where("groupActivity2026.response", "in", ["join", "decline"])
      );
      const snap = await getDocs(q);
      const acc = [];
      snap.forEach((d) => {
        const data = d.data() || {};
        if (is4kiPaidUser(data)) {
          acc.push(projectUser(d.id, data));
        }
      });
      setUsers(acc);
      setLastFetchedAt(t);
      saveCache({ users: acc, lastFetchedAt: t, pendingLoaded: false });
    } catch (err) {
      console.error("GroupActivity2026List responded fetch error:", err);
      setErrorMsg("回答済みユーザーの取得に失敗しました。");
    } finally {
      setSyncing(false);
      setLoading(false);
    }
  };

  // ───────────────────────────────────────────
  // 差分取得（lastFetchedAt 以降の応答だけ）
  // ───────────────────────────────────────────
  const loadDelta = async (since) => {
    setSyncing(true);
    setErrorMsg("");
    const t = new Date();
    try {
      const q = query(
        collection(db, "users"),
        where("groupActivity2026.respondedAt", ">", Timestamp.fromDate(since))
      );
      const snap = await getDocs(q);
      const changed = [];
      snap.forEach((d) => {
        const data = d.data() || {};
        if (is4kiPaidUser(data)) {
          changed.push(projectUser(d.id, data));
        }
      });
      const byId = new Map(users.map((u) => [u.id, u]));
      changed.forEach((u) => byId.set(u.id, u));
      const merged = Array.from(byId.values());
      setUsers(merged);
      setLastFetchedAt(t);
      saveCache({ users: merged, lastFetchedAt: t, pendingLoaded });
    } catch (err) {
      console.error("GroupActivity2026List delta fetch error:", err);
      console.warn("差分取得失敗。回答済み再取得にフォールバックします。");
      await loadRespondedOnly();
    } finally {
      setSyncing(false);
    }
  };

  // ───────────────────────────────────────────
  // 全件取得（未回答含む）：明示的アクションでのみ
  // ───────────────────────────────────────────
  const loadAllIncludingPending = async () => {
    setSyncing(true);
    setErrorMsg("");
    const t = new Date();
    try {
      const snap = await getDocs(collection(db, "users"));
      const acc = [];
      snap.forEach((d) => {
        const data = d.data() || {};
        if (is4kiPaidUser(data)) {
          acc.push(projectUser(d.id, data));
        }
      });
      setUsers(acc);
      setPendingLoaded(true);
      setLastFetchedAt(t);
      saveCache({ users: acc, lastFetchedAt: t, pendingLoaded: true });
    } catch (err) {
      console.error("GroupActivity2026List full fetch error:", err);
      setErrorMsg("全件取得に失敗しました。");
    } finally {
      setSyncing(false);
    }
  };

  // ───────────────────────────────────────────
  // マウント時：キャッシュ復元 → 差分 or 初回
  // ───────────────────────────────────────────
  useEffect(() => {
    const cache = loadCache();
    if (cache && cache.users.length > 0) {
      setUsers(cache.users);
      setLastFetchedAt(new Date(cache.lastFetchedAt));
      setPendingLoaded(!!cache.pendingLoaded);
      setLoading(false);
      loadDelta(new Date(cache.lastFetchedAt));
    } else {
      loadRespondedOnly();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ───────────────────────────────────────────
  // 振り分け（カウント用）
  // ───────────────────────────────────────────
  const counts = useMemo(() => {
    const out = { join: 0, decline: 0, pending: 0 };
    users.forEach((u) => {
      const r = u.groupActivity2026?.response;
      if (r === "join") out.join++;
      else if (r === "decline") out.decline++;
      else out.pending++;
    });
    return out;
  }, [users]);

  // 汎用集計
  const aggregateBy = (keyOf, fallbackLabel) => {
    const map = new Map();
    users.forEach((u) => {
      const raw = keyOf(u);
      if (raw === null) return;
      const key = (raw || "").trim() || fallbackLabel;
      if (!map.has(key)) {
        map.set(key, { label: key, join: 0, decline: 0, pending: 0, total: 0 });
      }
      const entry = map.get(key);
      const r = u.groupActivity2026?.response;
      if (r === "join") entry.join++;
      else if (r === "decline") entry.decline++;
      else entry.pending++;
      entry.total = entry.join + entry.decline + entry.pending;
    });
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  };

  const byPrefecture = useMemo(
    () => aggregateBy((u) => u.prefecture, "未登録"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [users]
  );
  const byUsState = useMemo(
    () =>
      aggregateBy(
        (u) => (u.prefecture === "海外（アメリカ）" ? u.usState : null),
        "州未登録"
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [users]
  );
  const byCountry = useMemo(
    () =>
      aggregateBy(
        (u) => (u.prefecture === "海外（その他）" ? u.country : null),
        "国未登録"
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [users]
  );

  const handleRefreshDelta = () => {
    if (lastFetchedAt) loadDelta(lastFetchedAt);
    else loadRespondedOnly();
  };

  const handleFullReload = () => {
    clearCache();
    setUsers([]);
    setPendingLoaded(false);
    setLastFetchedAt(null);
    setLoading(true);
    loadRespondedOnly();
  };

  return (
    <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
      {/* ヘッダー */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate("/admin")} size="small">
          <ArrowBackRoundedIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography
            variant="overline"
            sx={{
              letterSpacing: "0.18em",
              fontSize: 11,
              opacity: 0.8,
              color: textBase,
            }}
          >
            ADMIN
          </Typography>
          <Typography
            variant="h5"
            sx={{ fontWeight: 800, letterSpacing: "0.03em", color: textBase }}
          >
            2026グループ参加状況
          </Typography>
        </Box>
        <Tooltip title="差分のみ取得（前回以降の更新分）">
          <span>
            <IconButton onClick={handleRefreshDelta} disabled={syncing}>
              <RefreshRoundedIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.65)", mb: 2 }}>
        4期（MM2026）支払い済みユーザーの「参加意思」を集計します。通常は回答済みのみ読み込み、未回答はボタンで明示的に取得します。
      </Typography>

      {/* 同期状態 */}
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 2 }}
      >
        <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.6)" }}>
          前回取得：
          {lastFetchedAt
            ? lastFetchedAt.toLocaleString("ja-JP")
            : "未取得"}
        </Typography>
        <Chip
          size="small"
          label={pendingLoaded ? "未回答も取得済み" : "回答済みのみ取得中"}
          sx={{
            bgcolor: pendingLoaded ? "#2e7d3214" : "rgba(0,0,0,0.06)",
            color: pendingLoaded ? "#2e7d32" : "rgba(0,0,0,0.65)",
            fontWeight: 700,
          }}
        />
        {syncing && (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <CircularProgress size={12} />
            <Typography variant="caption" sx={{ color: accent }}>
              同期中…
            </Typography>
          </Stack>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {!pendingLoaded && (
          <Button
            size="small"
            variant="contained"
            startIcon={<CloudDownloadRoundedIcon />}
            onClick={loadAllIncludingPending}
            disabled={syncing}
            sx={{
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 800,
              bgcolor: accent,
              "&:hover": { bgcolor: accent, filter: "brightness(0.95)" },
            }}
          >
            未回答ユーザーを取得
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          startIcon={<CloudDownloadRoundedIcon />}
          onClick={handleFullReload}
          disabled={syncing}
          sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}
        >
          完全に再取得（回答済みのみ）
        </Button>
      </Stack>

      {errorMsg && (
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 2,
            bgcolor: "rgba(220,60,60,0.08)",
            border: "1px solid rgba(220,60,60,0.3)",
            color: "rgb(160,30,30)",
            fontSize: 14,
          }}
        >
          {errorMsg}
        </Paper>
      )}

      {/* 集計サマリー */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5 },
          borderRadius: 3,
          border: `1px solid ${border}`,
          bgcolor: surface,
          boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
          mb: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ sm: "center" }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexGrow: 1,
            }}
          >
            <GroupsRoundedIcon sx={{ color: accent }} />
            <Typography sx={{ fontWeight: 700, color: textBase }}>
              取得済み合計：
            </Typography>
            <Typography sx={{ fontWeight: 900, color: accent, fontSize: 20 }}>
              {loading ? "…" : users.length}
            </Typography>
            <Typography sx={{ color: textBase }}>人</Typography>
            {!pendingLoaded && (
              <Typography
                variant="caption"
                sx={{ color: "rgba(0,0,0,0.55)", ml: 1 }}
              >
                （未回答ユーザーは未取得）
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {(["join", "decline", "pending"]).map((k) => {
              const cfg = BUCKETS[k];
              const Icon = cfg.icon;
              const labelCount =
                k === "pending" && !pendingLoaded ? "未取得" : counts[k];
              return (
                <Chip
                  key={k}
                  icon={<Icon style={{ color: cfg.color }} />}
                  label={`${cfg.label} ${labelCount}`}
                  sx={{
                    fontWeight: 800,
                    bgcolor: `${cfg.color}14`,
                    color: cfg.color,
                    border: `1px solid ${cfg.color}33`,
                  }}
                />
              );
            })}
          </Stack>
        </Stack>
      </Paper>

      {/* 都道府県別グラフ */}
      <DistributionChart
        title="都道府県別"
        subtitle="（取得済みユーザーを集計・人数が多い順）"
        data={byPrefecture}
        pendingLoaded={pendingLoaded}
        accent={accent}
        border={border}
        surface={surface}
        textBase={textBase}
      />

      {/* 海外（アメリカ）州別 */}
      {byUsState.length > 0 && (
        <DistributionChart
          title="海外（アメリカ）州別"
          subtitle={`（prefecture が「海外（アメリカ）」のユーザー ${byUsState.reduce((s, x) => s + x.total, 0)} 人を集計）`}
          data={byUsState}
          pendingLoaded={pendingLoaded}
          accent={accent}
          border={border}
          surface={surface}
          textBase={textBase}
        />
      )}

      {/* 海外（その他）国別 */}
      {byCountry.length > 0 && (
        <DistributionChart
          title="海外（その他）国別"
          subtitle={`（prefecture が「海外（その他）」のユーザー ${byCountry.reduce((s, x) => s + x.total, 0)} 人を集計）`}
          data={byCountry}
          pendingLoaded={pendingLoaded}
          accent={accent}
          border={border}
          surface={surface}
          textBase={textBase}
        />
      )}
    </Box>
  );
}

// ─────────────────────────────────────────────
// 共通：積み上げ縦棒チャート
//   data: [{ label, join, decline, pending, total }, ...]
// ─────────────────────────────────────────────
function DistributionChart({
  title,
  subtitle,
  data,
  pendingLoaded,
  accent,
  border,
  surface,
  textBase,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 3,
        border: `1px solid ${border}`,
        bgcolor: surface,
        boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
        mb: 2,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <BarChartRoundedIcon sx={{ color: accent }} />
        <Typography sx={{ fontWeight: 700, color: textBase }}>{title}</Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.55)" }}>
            {subtitle}
          </Typography>
        )}
      </Stack>

      {data.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography sx={{ color: "rgba(0,0,0,0.55)" }}>
            データがありません。
          </Typography>
        </Box>
      ) : (
        <Box sx={{ width: "100%", overflowX: "auto", overflowY: "hidden" }}>
          <Box
            sx={{
              width: Math.max(640, data.length * 38),
              height: 260,
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data}
                margin={{ top: 8, right: 16, left: 8, bottom: 56 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11 }}
                  angle={-45}
                  textAnchor="end"
                  interval={0}
                  height={56}
                />
                <YAxis type="number" allowDecimals={false} />
                <RTooltip formatter={(value, name) => [`${value} 人`, name]} />
                <Legend />
                <Bar
                  dataKey="join"
                  stackId="a"
                  fill={BUCKETS.join.color}
                  name="参加する"
                />
                <Bar
                  dataKey="decline"
                  stackId="a"
                  fill={BUCKETS.decline.color}
                  name="参加しない"
                >
                  {!pendingLoaded && (
                    <LabelList
                      dataKey="join"
                      position="top"
                      fill={BUCKETS.join.color}
                      fontSize={11}
                      fontWeight={800}
                      formatter={(v) => (v > 0 ? v : "")}
                    />
                  )}
                </Bar>
                {pendingLoaded && (
                  <Bar
                    dataKey="pending"
                    stackId="a"
                    fill={BUCKETS.pending.color}
                    name="未回答"
                  >
                    <LabelList
                      dataKey="join"
                      position="top"
                      fill={BUCKETS.join.color}
                      fontSize={11}
                      fontWeight={800}
                      formatter={(v) => (v > 0 ? v : "")}
                    />
                  </Bar>
                )}
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      )}

      {!pendingLoaded && (
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 1, color: "rgba(0,0,0,0.55)" }}
        >
          ※ 未回答ユーザーは未取得のためグラフに含まれていません。「未回答ユーザーを取得」ボタンから取得すると反映されます。
        </Typography>
      )}
    </Paper>
  );
}
